import React, { useCallback, useEffect, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import marketplaceService, {
    BuyerOrder,
    extractErrorMessage,
    MarketplaceInvoice,
} from '../../../services/marketplaceService';
import { formatEuro } from '../../../pages/marketplaceVisibility';
import MaterialSymbol from '../../common/MaterialSymbol';
import '../marketplace-design.css';

/** Bestellstatus → Status-Pill der Vorlage (ok / warn / err / neu). */
const ORDER_STATUS_TONE: Record<string, string> = {
    paid: 'ok',
    completed: 'ok',
    pending: 'warn',
    partially_refunded: 'warn',
    payment_failed: 'err',
    cancelled: 'neu',
    refunded: 'neu',
};

/** Belegart → Pill: Storno rot, sonst neutral. */
const DOCUMENT_TONE: Record<string, string> = { cancellation: 'err' };

const formatDate = (value: string | null | undefined): string =>
    value ? new Date(value).toLocaleDateString('de-DE') : '–';

/** Speichert ein Blob über einen temporären Link (Bearer-geschützte PDFs lassen sich nicht direkt verlinken). */
const saveBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

/**
 * Bestellhistorie und Rechnungen (Plan T6). Bestellungen kommen aus
 * `GET /marketplace/orders`, Rechnungen aus `GET /marketplace/invoices` (T4,
 * Empfänger-Sicht) mit PDF-Download. Belastungsanzeigen für interne
 * Verrechnungspreise sind kein steuerlicher Beleg und werden entsprechend
 * benannt.
 */
const BuyerOrdersPanel: React.FC = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const [orders, setOrders] = useState<BuyerOrder[]>([]);
    const [invoices, setInvoices] = useState<MarketplaceInvoice[]>([]);
    const [invoicesUnavailable, setInvoicesUnavailable] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [downloading, setDownloading] = useState<string | null>(null);

    const load = useCallback(async () => {
        setError(null);
        const [ordersResult, invoicesResult] = await Promise.allSettled([
            marketplaceService.getOrders(),
            marketplaceService.getInvoices(),
        ]);
        if (ordersResult.status === 'fulfilled') setOrders(ordersResult.value);
        else setError(extractErrorMessage(ordersResult.reason, translate('marketplaceBuyer.orders.loadError')));
        if (invoicesResult.status === 'fulfilled') {
            setInvoices(invoicesResult.value);
            setInvoicesUnavailable(false);
        } else {
            setInvoicesUnavailable(true);
        }
        setLoading(false);
    }, [translate]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const download = async (invoice: MarketplaceInvoice) => {
        setDownloading(invoice.uuid);
        try {
            const blob = await marketplaceService.downloadInvoicePdf(invoice.uuid);
            saveBlob(blob, `${invoice.invoiceNumber}.pdf`);
        } catch (err) {
            notify(extractErrorMessage(err, translate('marketplaceBuyer.orders.downloadError')), { type: 'error' });
        } finally {
            setDownloading(null);
        }
    };

    if (loading) return <div className="mk-skel" aria-busy="true" />;

    const statusPill = (order: BuyerOrder) => (
        <span className={`mk-pill ${ORDER_STATUS_TONE[order.status] ?? 'neu'}`}>
            {translate(`marketplaceBuyer.orders.statuses.${order.status}`, { _: order.status })}
        </span>
    );
    const amount = (order: BuyerOrder) =>
        Number(order.total) === 0 ? translate('marketplaceBuyer.price.free') : formatEuro(order.total, order.currency);
    const licenseLabel = (item: BuyerOrder['items'][number]) => {
        if (!item.licenseType) return null;
        const base = `${translate(`marketplaceBuyer.licenseType.${item.licenseType}.chip`)}${item.licenseType === 'seat_copy' && item.seats ? ` (${item.seats})` : ''}`;
        // Bundles: eine Lizenz je Kurs an derselben Position
        return (item.licenseCount ?? 0) > 1 ? `${base} · ${translate('marketplaceBuyer.bundle.licenseCount', { count: item.licenseCount })}` : base;
    };
    const docPill = (invoice: MarketplaceInvoice) => (
        <span className={`mk-pill ${DOCUMENT_TONE[invoice.documentType] ?? 'neu'}`}>
            {translate(`marketplaceBuyer.orders.documentTypes.${invoice.documentType}`, { _: invoice.documentType })}
        </span>
    );
    const pdfButton = (invoice: MarketplaceInvoice) => (
        <button
            type="button"
            disabled={downloading === invoice.uuid}
            onClick={() => download(invoice)}
            aria-label={translate('marketplaceBuyer.orders.download', { number: invoice.invoiceNumber })}
            title={translate('marketplaceBuyer.orders.pdf')}
        >
            <MaterialSymbol name="download" />
        </button>
    );

    return (
        <div>
            {error && (
                <div className="mk-alert err" role="alert">
                    <MaterialSymbol name="error" />
                    <div className="body">{error}</div>
                </div>
            )}

            <section aria-labelledby="orders-heading">
                <div className="mk-sec-t">
                    <div>
                        <h2 id="orders-heading">{translate('marketplaceBuyer.orders.title')}</h2>
                        <p>{translate('marketplaceBuyer.orders.intro')}</p>
                    </div>
                </div>
                {orders.length === 0 ? (
                    <div className="mk-empty" role="status">
                        <MaterialSymbol name="receipt_long" />
                        <p>{translate('marketplaceBuyer.orders.empty')}</p>
                    </div>
                ) : (
                    <>
                        <div className="mk-tbl-wrap wide">
                            <table className="mk-tbl">
                                <thead>
                                    <tr>
                                        <th>{translate('marketplaceBuyer.orders.date')}</th>
                                        <th>{translate('marketplaceBuyer.orders.number')}</th>
                                        <th>{translate('marketplaceBuyer.orders.item')}</th>
                                        <th className="r">{translate('marketplaceBuyer.orders.amount')}</th>
                                        <th>{translate('marketplaceBuyer.orders.settlement')}</th>
                                        <th>{translate('marketplaceBuyer.orders.status')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {orders.map((order) => (
                                        <tr key={order.orderId} data-testid={`order-row-${order.orderNumber ?? order.orderId}`}>
                                            <td className="mono">{formatDate(order.createdAt)}</td>
                                            <td className="mono">{order.orderNumber ?? '–'}</td>
                                            <td>
                                                {order.items.map((item, i) => (
                                                    <div key={i}>
                                                        <b>{item.title ?? '–'}</b>
                                                        {licenseLabel(item) && <span className="sub">{licenseLabel(item)}</span>}
                                                    </div>
                                                ))}
                                            </td>
                                            <td className="r">{amount(order)}</td>
                                            <td className="mut">{translate(`marketplaceBuyer.orders.settlements.${order.settlement}`)}</td>
                                            <td>{statusPill(order)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {/* schmal: Karten statt 6-spaltiger Tabelle (Marktplatz-Nachlauf) */}
                        <div className="mk-cards" data-testid="order-cards">
                            {orders.map((order) => (
                                <div key={order.orderId} className="mk-card">
                                    <div className="rowf" style={{ marginTop: 0 }}>
                                        <span className="t">{order.orderNumber ?? '–'}</span>
                                        {statusPill(order)}
                                    </div>
                                    <div className="m">{formatDate(order.createdAt)}</div>
                                    {order.items.map((item, i) => (
                                        <div key={i} style={{ marginTop: 6, wordBreak: 'break-word' }}>
                                            {item.title ?? '–'}
                                            {licenseLabel(item) && <span className="m"> · {licenseLabel(item)}</span>}
                                        </div>
                                    ))}
                                    <div className="rowf">
                                        <span className="t">{amount(order)}</span>
                                        <span className="m">{translate(`marketplaceBuyer.orders.settlements.${order.settlement}`)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </section>

            <section aria-labelledby="invoices-heading" className="mk-gap-top">
                <div className="mk-sec-t">
                    <div>
                        <h2 id="invoices-heading">{translate('marketplaceBuyer.orders.invoicesTitle')}</h2>
                        <p>{translate('marketplaceBuyer.orders.invoicesIntro')}</p>
                    </div>
                </div>
                {invoicesUnavailable ? (
                    <div className="mk-alert info" role="status">
                        <MaterialSymbol name="info" />
                        <div className="body">{translate('marketplaceBuyer.orders.invoicesUnavailable')}</div>
                    </div>
                ) : invoices.length === 0 ? (
                    <div className="mk-empty" role="status">
                        <MaterialSymbol name="description" />
                        <p>{translate('marketplaceBuyer.orders.invoicesEmpty')}</p>
                    </div>
                ) : (
                    <>
                        <div className="mk-tbl-wrap wide">
                            <table className="mk-tbl">
                                <thead>
                                    <tr>
                                        <th>{translate('marketplaceBuyer.orders.invoiceNumber')}</th>
                                        <th>{translate('marketplaceBuyer.orders.invoiceType')}</th>
                                        <th>{translate('marketplaceBuyer.orders.date')}</th>
                                        <th>{translate('marketplaceBuyer.orders.issuer')}</th>
                                        <th className="r">{translate('marketplaceBuyer.orders.amount')}</th>
                                        <th className="r">{translate('marketplaceBuyer.orders.pdf')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoices.map((invoice) => (
                                        <tr key={invoice.uuid}>
                                            <td className="mono"><b>{invoice.invoiceNumber}</b></td>
                                            <td>{docPill(invoice)}</td>
                                            <td className="mono">{formatDate(invoice.issueDate)}</td>
                                            <td>{invoice.issuerName ?? '–'}</td>
                                            <td className="r">{formatEuro(invoice.total, invoice.currency)}</td>
                                            <td><div className="mk-ico-act">{pdfButton(invoice)}</div></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="mk-cards" data-testid="invoice-cards">
                            {invoices.map((invoice) => (
                                <div key={invoice.uuid} className="mk-card">
                                    <div className="rowf" style={{ marginTop: 0 }}>
                                        <span className="t">{invoice.invoiceNumber}</span>
                                        {docPill(invoice)}
                                    </div>
                                    <div className="m">{formatDate(invoice.issueDate)} · {invoice.issuerName ?? '–'}</div>
                                    <div className="rowf">
                                        <span className="t">{formatEuro(invoice.total, invoice.currency)}</span>
                                        <button
                                            type="button"
                                            className="mk-btn-o mk-sm"
                                            disabled={downloading === invoice.uuid}
                                            onClick={() => download(invoice)}
                                            aria-label={translate('marketplaceBuyer.orders.download', { number: invoice.invoiceNumber })}
                                        >
                                            <MaterialSymbol name="download" />
                                            {translate('marketplaceBuyer.orders.pdf')}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </section>
        </div>
    );
};

export default BuyerOrdersPanel;
